import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthToken, getApiUrl } from '../utils/api';
import { useAuth } from './AuthContext';

interface ProjectFile {
  id: string;
  file_name: string;
  stored_name: string;
  file_size: number;
  file_type: string;
  upload_date: string;
  category?: string;
  // Store the actual file object for local projects
  fileObject?: File;
}

interface Project {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  files: ProjectFile[];
  // Legacy fields for compatibility
  fileCount?: number;
  queryCount?: number;
  isSecured?: boolean;
  organization?: string;
}

type ProgressCallback = (fileName: string, progress: number) => void;

interface VaultContextType {
  projects: Project[];
  createProject: (name: string) => Promise<Project>;
  updateProject: (projectId: string, updates: Partial<Project>) => void;
  deleteProject: (projectId: string) => void;
  getProject: (projectId: string) => Project | undefined;
  addFilesToProject: (projectId: string, files: File[], onProgress?: ProgressCallback) => Promise<void>;
  removeFileFromProject: (projectId: string, fileId: string) => Promise<void>;
  refreshProjects: () => Promise<void>;
}

const VaultContext = createContext<VaultContextType | undefined>(undefined);

export const useVault = () => {
  const context = useContext(VaultContext);
  if (!context) {
    throw new Error('useVault must be used within a VaultProvider');
  }
  return context;
};

export const VaultProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Save projects to localStorage whenever projects change (excluding fileObject)
  useEffect(() => {
    const token = getAuthToken();
    
    // Only save to localStorage for unauthenticated users or when backend fails
    // Authenticated users should use backend storage only
    if (!token && projects.length > 0) {
      try {
        // Remove fileObject before saving since File objects can't be serialized
        const projectsToSave = projects.map(project => ({
          ...project,
          files: project.files.map(file => {
            const { fileObject, ...fileWithoutObject } = file;
            return fileWithoutObject;
          })
        }));
        localStorage.setItem('vault_projects', JSON.stringify(projectsToSave));
        console.log('Projects saved to localStorage (without file objects):', projects.length);
      } catch (error) {
        console.error('Error saving projects to localStorage:', error);
      }
    }
  }, [projects]);

  // Load projects from backend or localStorage on mount
  useEffect(() => {
    // Don't load projects until auth has finished loading
    if (authLoading) {
      console.log('Auth still loading, waiting to load projects...');
      return;
    }

    const loadProjects = async () => {
      const token = getAuthToken();
      
      if (isAuthenticated && token) {
        // Try to load from backend if authenticated
        try {
          console.log('Loading projects from backend...');
          const response = await fetch(getApiUrl('projects'), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Projects loaded from backend:', data);
            
            // Validate response structure
            if (!data || typeof data.projects !== 'object') {
              console.error('Invalid response structure from backend:', data);
              return; // Don't clear existing projects on invalid response
            }
            
            try {
              // Convert object to array and ensure compatibility
              const projectsArray = Object.values(data.projects).map((proj: any) => {
                // Validate project structure
                if (!proj || typeof proj !== 'object') {
                  console.warn('Invalid project structure:', proj);
                  return null;
                }
                
                return {
                  ...proj,
                  fileCount: Array.isArray(proj.files) ? proj.files.length : 0,
                  isSecured: true,
                  queryCount: 0
                };
              }).filter(Boolean); // Remove any null entries
              
              console.log('Processed projects array:', projectsArray);
              setProjects(projectsArray as Project[]);
              
              // Clear localStorage when successfully loading from backend
              // This prevents data leakage between users
              localStorage.removeItem('vault_projects');
              return; // Successfully loaded from backend, don't load from localStorage
            } catch (processingError) {
              console.error('Error processing projects data:', processingError);
              // Don't clear existing projects on processing error
              return;
            }
          } else {
            console.error('Failed to load projects, status:', response.status);
            const errorText = await response.text();
            console.error('Error response:', errorText);
          }
        } catch (error) {
          console.error('Error loading projects from backend:', error);
        }
      }
      
      // Load from localStorage if not authenticated or backend failed
      if (!isAuthenticated) {
        // Only clear projects state when loading from localStorage
        setProjects([]);
        
        const savedProjects = localStorage.getItem('vault_projects');
        if (savedProjects) {
          try {
            const parsedProjects = JSON.parse(savedProjects);
            setProjects(parsedProjects);
            console.log('Loaded projects from localStorage:', parsedProjects.length);
          } catch (err) {
            console.error('Error loading projects from localStorage:', err);
            // Set empty array on localStorage parsing error
            setProjects([]);
          }
        } else {
          // Set empty array if no localStorage data
          setProjects([]);
        }
      }
    };
    
    loadProjects();
  }, [authLoading, isAuthenticated]);

  const createProject = async (name: string): Promise<Project> => {
    const token = getAuthToken();
    
    // Try authenticated creation first, fallback to local if not authenticated
    if (isAuthenticated && token) {
      try {
      const response = await fetch(getApiUrl('projects'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: name.trim() })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create project');
      }
      
      const newProject = await response.json();
      console.log('Project created on backend:', newProject);
      const enhancedProject = {
        ...newProject,
        fileCount: 0,
        isSecured: true,
        queryCount: 0
      };
      
      setProjects(prev => [...prev, enhancedProject]);
      console.log('Projects after creation:', [...projects, enhancedProject]);
      return enhancedProject;
      } catch (error) {
        console.error('Error creating project:', error);
      }
    }
    
    // Fallback to local creation (when not authenticated or server error)
    const newProject: Project = {
      id: Date.now().toString(),
      name: name.trim(),
      user_id: 'local',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      files: [],
      fileCount: 0,
      queryCount: 0,
      isSecured: true
    };
    
    setProjects(prev => [...prev, newProject]);
    return newProject;
  };

  const updateProject = (projectId: string, updates: Partial<Project>) => {
    setProjects(prev => prev.map(project =>
      project.id === projectId
        ? { ...project, ...updates }
        : project
    ));
  };

  const deleteProject = async (projectId: string) => {
    const token = getAuthToken();
    
    console.log('=== DELETE PROJECT CALLED ===');
    console.log('Project ID:', projectId);
    console.log('Is authenticated:', isAuthenticated);
    console.log('Has token:', !!token);
    console.log('Token value:', token);
    
    if (isAuthenticated && token) {
      try {
        console.log('🗑️ Deleting project from backend:', projectId);
        const deleteUrl = getApiUrl(`projects/${projectId}`);
        console.log('📡 DELETE request URL:', deleteUrl);
        
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('✅ DELETE request completed. Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Delete failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Failed to delete project: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('🎉 Project deleted from backend successfully:', result);
        
        // Update local state after successful backend deletion
        setProjects(prev => {
          const updated = prev.filter(project => project.id !== projectId);
          console.log('Updated projects after deletion:', updated.length, 'projects remaining');
          return updated;
        });
        return;
      } catch (error) {
        console.error('❌ Error deleting project from backend:', error);
        throw error;
      }
    } else {
      console.log('⚠️ Cannot delete from backend:', {
        isAuthenticated,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 10)}...` : 'none'
      });
    }
    
    // Fallback to local deletion when not authenticated
    console.log('💾 Deleting project locally (not authenticated)');
    setProjects(prev => prev.filter(project => project.id !== projectId));
  };

  const getProject = (projectId: string): Project | undefined => {
    return projects.find(project => project.id === projectId);
  };

  const addFilesToProject = async (projectId: string, files: File[], onProgress?: ProgressCallback) => {
    const token = getAuthToken();
    console.log('=== ADD FILES TO PROJECT CALLED ===');
    console.log('Project ID:', projectId);
    console.log('Files count:', files.length);
    console.log('Is authenticated:', isAuthenticated);
    console.log('Has token:', !!token);
    console.log('Token value:', token);
    
    if (isAuthenticated && token) {
      console.log('🚀 Starting authenticated file upload...');
      
      // Upload files one by one to track progress for each
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`📤 Uploading file ${i + 1}/${files.length}: ${file.name}`);
        
        if (onProgress) {
          onProgress(file.name, 0);
        }
        
        try {
          const formData = new FormData();
          
          // Add single file to FormData
          if (file.type.startsWith('image/')) {
            formData.append('images', file);
          } else {
            formData.append('files', file);
          }
          
          const uploadUrl = getApiUrl(`projects/${projectId}/files`);
          console.log('📡 Sending request to:', uploadUrl);
          
          // Create XMLHttpRequest for progress tracking
          const uploadPromise = new Promise<any>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable && onProgress) {
                const progress = Math.round((event.loaded / event.total) * 100);
                onProgress(file.name, progress);
              }
            };
            
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const result = JSON.parse(xhr.responseText);
                  resolve(result);
                } catch (e) {
                  reject(new Error('Invalid JSON response'));
                }
              } else {
                if (xhr.status === 413) {
                  reject(new Error('File too large. Please try uploading a smaller file or contact your administrator to increase the upload limit.'));
                } else {
                  reject(new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`));
                }
              }
            };
            
            xhr.onerror = () => {
              reject(new Error('Network error during upload'));
            };
            
            xhr.open('POST', uploadUrl);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(formData);
          });
          
          const result = await uploadPromise;
          console.log(`✅ File ${file.name} uploaded successfully`);
          
          if (onProgress) {
            onProgress(file.name, 100);
          }
          
          // Update local state with the updated project (after each file)
          setProjects(prev => prev.map(project =>
            project.id === projectId
              ? {
                  ...result.project,
                  fileCount: result.project.files.length,
                  isSecured: true,
                  queryCount: project.queryCount || 0
                }
              : project
          ));
          
        } catch (error) {
          console.error(`❌ Error uploading file ${file.name}:`, error);
          if (onProgress) {
            onProgress(file.name, -1); // -1 indicates error
          }
          throw error;
        }
      }
      
      console.log('🎉 All files uploaded successfully');
      return; // Successfully uploaded to backend, don't continue to local fallback
    } else {
      console.log('⚠️ Cannot upload to backend:', {
        isAuthenticated,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 10)}...` : 'none'
      });
    }
    
    // Fallback to local file handling when not authenticated
    console.log('💾 Using local file storage (not authenticated)');
    // Create mock ProjectFile objects for local storage with actual file objects
    const newFiles: ProjectFile[] = files.map((file, index) => ({
      id: `local_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
      file_name: file.name,
      stored_name: file.name,
      file_size: file.size,
      file_type: file.type,
      upload_date: new Date().toISOString(),
      category: file.type.startsWith('image/') ? 'image' : 'document',
      fileObject: file  // Store the actual File object
    }));
    
    // Update local state with new files
    setProjects(prev => prev.map(project =>
      project.id === projectId
        ? {
            ...project,
            files: [...project.files, ...newFiles],
            fileCount: project.files.length + newFiles.length
          }
        : project
    ));
  };

  const removeFileFromProject = async (projectId: string, fileId: string) => {
    const token = getAuthToken();
    
    if (isAuthenticated && token) {
      try {
      const response = await fetch(getApiUrl(`projects/${projectId}/files/${fileId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
      
      const result = await response.json();
      
      // Update local state with the updated project
      setProjects(prev => prev.map(project =>
        project.id === projectId
          ? {
              ...result.project,
              fileCount: result.project.files.length,
              isSecured: true,
              queryCount: project.queryCount || 0
            }
          : project
      ));
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
    
    // Fallback to local removal (when not authenticated or server error)
    setProjects(prev => prev.map(project =>
      project.id === projectId
        ? {
            ...project,
            files: project.files.filter(file => file.id !== fileId),
            fileCount: project.files.filter(file => file.id !== fileId).length
          }
        : project
    ));
  };
  
  const refreshProjects = async () => {
    const token = getAuthToken();
    if (!isAuthenticated || !token) return;
    
    try {
      const response = await fetch(getApiUrl('projects'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const projectsArray = Object.values(data.projects).map((proj: any) => ({
          ...proj,
          fileCount: proj.files.length,
          isSecured: true,
          queryCount: 0
        }));
        setProjects(projectsArray as Project[]);
      }
    } catch (error) {
      console.error('Error refreshing projects:', error);
    }
  };

  const value: VaultContextType = {
    projects,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    addFilesToProject,
    removeFileFromProject,
    refreshProjects
  };

  return (
    <VaultContext.Provider value={value}>
      {children}
    </VaultContext.Provider>
  );
};